import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, ConfirmEventType, MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { SecurityGateway } from 'src/app/application/gateway/SecurityGateway';
import { Profile } from 'src/app/domain/models/Profile';
import { TokenResponse } from 'src/app/domain/models/TokenResponse';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';
import { AuthRedirectService } from 'src/app/ui/util/auth-redirect.service';
import { ProfileUtils } from 'src/app/ui/util/profile-utils';

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.css'],
  providers: [ConfirmationService]
})
export class AdminUsersComponent implements OnInit {

  profiles: Profile[] = []
  filteredProfiles: Profile[] = []
  selected: Profile | null = null
  profile: BasicUser | undefined
  searchTerm: string = '';
  loading: boolean = false

  selectedProfile: Profile = {} as Profile;
  showSendInvitationCodeDialog: boolean = false;
  showUserCreateDialog: boolean = false;
  showUserDetailDialog: boolean = false;
  showUserEditDialog: boolean = false;
  showImportersAssociationDialog: boolean = false;
  showConsigneeNotifierAssociationDialog: boolean = false;
  mockImporter: boolean = false;
  exporterRelation: number | undefined = undefined;

  constructor(
    private _router: Router,
    private _messageService: MessageService,
    private _confirmationService: ConfirmationService,
    private _authService: AuthGateway,
    private _profileService: ProfileGateway,
    private readonly translate: TranslateService,
    private _securityGateway: SecurityGateway,
    private _authRedirectService: AuthRedirectService
  ){
  }

  ngOnInit(): void {
    this.profile = this._authService.getUser()
    this.loadProfiles()
  }

  loadProfiles() {
    this.searchTerm = ''
    this._profileService.getAll().subscribe({
      next: (v) => {
        this.profiles = v
        this.filteredProfiles = v
      },
      error: (e) => {
        this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: '' });
      }
    })
  }

  adminLogin(profile: Profile) {
    this._securityGateway.adminLogin(profile.contactEmail).subscribe({
      next: (v: TokenResponse) => {
        this._authService.authenticate(v.data.accessToken, v.data.refreshToken)
        this._authRedirectService.handlePostLoginRedirect().subscribe({
        next: () => {
          this.loading = false;
        },
        error: (e) => {
          this.loading = false;
          console.log(e)
        }
      })
      },
      error: (e) => {
        this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: '' });
      }
    })
  }

  getProfileTypeName(profileType: number): string {
    return ProfileUtils.getTypeName(profileType)
  }

  onOpenUserDetailDialog(profile: Profile) {
    this.showUserDetailDialog = true;
    this.selectedProfile = profile;
  }

  onCloseUserDetailDialog() {
    this.showUserDetailDialog = false;
  }

  onOpenUserEditDialog(profile: Profile) {
    this.showUserEditDialog = true;
    this.selectedProfile = profile;
  }

  onCloseUserEditDialog(isReload?: boolean) {
    if (isReload) {
      this._messageService.add({
        severity: 'success',
        summary: this.translate.instant('AdminUsersManagement.Messages.SuccessfulActionTitle'),
        detail: this.translate.instant('AdminUsersManagement.Messages.UserEditDetail')
      });
      this.loadProfiles();
    }
    this.showUserEditDialog = false;
  }

  onOpenImportersAssociationDialog(profile: Profile) {
    this.showImportersAssociationDialog = true;
    this.selectedProfile = profile;
  }

  onCloseImportersAssociationDialog() {
    this.showImportersAssociationDialog = false;
  }

  onOpenConsigneeNotifierAssociationDialog(profile: Profile) {
    this.showConsigneeNotifierAssociationDialog = true;
    this.selectedProfile = profile;
  }

  onCloseConsigneeNotifierAssociationDialog() {
    this.showConsigneeNotifierAssociationDialog = false;
  }

  onOpenSendInvitationCodeDialog() {
    this.showSendInvitationCodeDialog = true;
  }

  onCloseSendInvitationCodeDialog() {
    this.showSendInvitationCodeDialog = false;
  }

  onOpenUserCreateDialog() {
    this.showUserCreateDialog = true;
  }

  onCloseUserCreateDialog(isReload?: boolean) {
    if (isReload) {
      this._messageService.add({
        severity: 'success',
        summary: this.translate.instant('AdminUsersManagement.Messages.SuccessfulActionTitle'),
        detail: this.translate.instant('AdminUsersManagement.Messages.UserCreatedDetail')
      });
      this.loadProfiles();
    }
    this.showUserCreateDialog = false;
  }

  toogleIsActive(profileSelected: any) {
    if(profileSelected) {
      this._confirmationService.confirm({
        message: profileSelected?.isActive ?
          this.translate.instant('AdminUsersManagement.Messages.DeactivateUserDetail', {value: profileSelected.contactName}) :
          this.translate.instant('AdminUsersManagement.Messages.ActivateUserDetail', {value: profileSelected.contactName}),
        header: profileSelected?.isActive ?
          this.translate.instant('AdminUsersManagement.Messages.DeactivateUserTitle') :
          this.translate.instant('AdminUsersManagement.Messages.ActivateUserTitle'),
        icon: `${profileSelected?.isActive ? 'pi pi-user-minus' : 'pi pi-user-plus'}`,
        acceptLabel: this.translate.instant('AdminUsersManagement.Messages.ConfirmButton'),
        rejectLabel: this.translate.instant('AdminUsersManagement.Messages.CancelButton'),
        acceptIcon: 'pi',
        rejectIcon: 'pi',
        accept: () => {
          const request: Profile = {
            ...profileSelected,
            updaterUsername: ""
          }

          this._profileService.toogleIsActivate(request).subscribe({
              next: () => {
                this.selected = null
                this._messageService.add({
                  severity: 'info',
                  summary: this.translate.instant('AdminUsersManagement.Messages.UpdatedStatusTitle'),
                  detail: this.translate.instant('AdminUsersManagement.Messages.UpdatedStatusDetail')
                })
                this.loadProfiles()
              },
              error: () => {
                this._messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('AdminUsersManagement.Messages.FailedActionTitle'),
                  detail: this.translate.instant('AdminUsersManagement.Messages.FailedActionDetail')
                });
              }
            })
        },
        reject: (type: any) => {
          switch (type) {
            case ConfirmEventType.REJECT:
              break;
            case ConfirmEventType.CANCEL:
              break;
          }
        }
      })
    }
  }

  sendValidationToken(profileId: number) {
    if(profileId) {
      this._confirmationService.confirm({
        header: this.translate.instant('AdminUsersManagement.Messages.ForwardingConfirmationTitle'),
        message: this.translate.instant('AdminUsersManagement.Messages.ForwardingConfirmationDetail'),
        acceptLabel: this.translate.instant('AdminUsersManagement.Messages.ConfirmButton'),
        rejectLabel: this.translate.instant('AdminUsersManagement.Messages.CancelButton'),
        icon: 'pi pi-envelope',
        acceptIcon: 'pi',
        rejectIcon: 'pi',
        accept: () => {
            this._profileService.sendValidationToken(profileId).subscribe({
              next: (v) => {
                this.selected = null
                this._messageService.add({
                  severity: 'success',
                  summary: this.translate.instant('AdminUsersManagement.Messages.SentInvitationTitle'),
                  detail: this.translate.instant('AdminUsersManagement.Messages.SentInvitationDetail')
                })
                this.loadProfiles()
              },
              error: (e) => {
                let errorMessage = ''
                switch(this.profile?.lang) {
                  case 'es':
                    errorMessage = e.error.messageEs
                    break
                  case 'en':
                    errorMessage = e.error.messageEn
                    break
                  default:
                    errorMessage = this.translate.instant('AdminUsersManagement.Messages.InvitationCodeErrorText')
                }
                this._messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('AdminUsersManagement.Messages.FailedActionTitle'),
                  detail: errorMessage
                });
              }
            })
        }
      })
    }
  }

  goToExporterImporters(exporterId: number) {
    this._router.navigate(['/' + Routes.adminExporterImportersPage, exporterId])
  }

  onFilter() {
    this.filteredProfiles = this.profiles.filter(item => this.applyFilters(item));
  }

  applyFilters(item: Profile) {
    if (this.searchTerm.trim()) {
      const isIdUser = this.compare(item.id);
      const isContactName = this.compare(item.contactName);
      const isEmail = this.compare(item.contactEmail);
      const isRole = this.compare(this.getProfileTypeName(item.type));
      const isCompany = this.compare(item.companyName);
      const isActive = this.compare(item.isActive, 'status');

      return isIdUser || isContactName || isEmail || isRole || isCompany || isActive
    }

    return true;
  }

  compare(value: any, type?: string): boolean {
    let isFound = false;
    if (type === 'status') {
      isFound = this.getStatusName(value)?.toLocaleLowerCase().includes(this.searchTerm.toLowerCase())
    }
    else {
      isFound = value?.toString()?.toLocaleLowerCase().includes(this.searchTerm.toLowerCase())
    }

    return value?.toString() ? isFound : false
  }

  getStatusName(value: boolean): string {
    if (value === true) return this.translate.instant('AdminUsersManagement.Table.ActiveText')
    else if (value === false) return this.translate.instant('AdminUsersManagement.Table.InactiveText')
    return ''
  }

  createImporterRelation(profile: Profile) {

    this.mockImporter = true
    this.exporterRelation = profile.id
    this.showUserCreateDialog = true;
  }
}
